/**
 * AzureDevOpsConnectionForm — "Add Azure DevOps Connection".
 *
 * Registers an Azure DevOps connection (organization + credential) so LeftShield can scan its
 * repositories. `POST` via `useCreateAzureDevOpsConnection()`.
 *
 * Pattern: `react-hook-form` + `zodResolver`, schema imported from
 * `AzureDevOpsConnection.schema.ts` (never redefined), Fluent `Field` for verbatim validation
 * messages, server errors mapped via the shared `applyServerErrors`
 * (Charter M3 / form-standard.context.md). The outer `Dialog` chrome is owned by
 * `IntegrationsPage.tsx` (this feature's own `components/`, not `src/pages/**`) — this component
 * renders the form CONTENT only and keeps its existing `onSuccess`/`onCancel` prop contract so the
 * hosting page needs no change.
 */
import type { FC } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Field, Input, Select, makeStyles, tokens } from '@fluentui/react-components';
import { PlugConnectedAddRegular } from '@fluentui/react-icons';
import { useCreateAzureDevOpsConnection } from '../hooks';
import type { CreateAzureDevOpsConnectionRequest } from '../types';
import { applyServerErrors, useReportFormDirty, FormActions, ServerErrorBanner } from '../../../components/forms';
import { AZURE_DEVOPS_AUTH_TYPE_OPTIONS } from './connectionFormFields';
import {
  AzureDevOpsConnectionSchema,
  type AzureDevOpsConnectionFormValues,
  type AzureDevOpsConnectionFormOutput,
} from './AzureDevOpsConnection.schema';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

// Known request-DTO field keys — used by the server-error -> field mapping.
const KNOWN_FIELDS = ['name', 'organizationUrl', 'authType', 'accessToken'] as const;

const DEFAULT_VALUES: AzureDevOpsConnectionFormValues = {
  name: '',
  organizationUrl: 'https://dev.azure.com/',
  authType: 'PersonalAccessToken',
  accessToken: '',
};

export interface AzureDevOpsConnectionFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Reports dirty state up so the hosting dialog can guard an unsaved-changes dismiss. */
  onDirtyChange?: (dirty: boolean) => void;
}

export const AzureDevOpsConnectionForm: FC<AzureDevOpsConnectionFormProps> = ({
  onSuccess,
  onCancel,
  onDirtyChange,
}) => {
  const styles = useStyles();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<AzureDevOpsConnectionFormValues, unknown, AzureDevOpsConnectionFormOutput>({
    resolver: zodResolver(AzureDevOpsConnectionSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  useReportFormDirty(isDirty, onDirtyChange);

  const authType = watch('authType');

  const [globalError, setGlobalError] = useState<string | undefined>();

  const createConnection = useCreateAzureDevOpsConnection();

  const onValid = (values: AzureDevOpsConnectionFormOutput) => {
    // Guard double-submit.
    if (createConnection.isPending) return;
    setGlobalError(undefined);

    const request: CreateAzureDevOpsConnectionRequest = {
      name: values.name,
      organizationUrl: values.organizationUrl,
      authType: values.authType,
      accessToken: values.accessToken,
    };

    createConnection.mutate(request, {
      onSuccess: () => {
        reset(DEFAULT_VALUES);
        onSuccess?.();
      },
      onError: (error) => {
        const { globalMessage } = applyServerErrors(error, KNOWN_FIELDS, setError);
        setGlobalError(globalMessage);
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onValid)} className={styles.form} noValidate>
      <ServerErrorBanner title="Error" message={globalError} testId="azure-devops-connection-form-server-error" />

      <Field
        label="Connection Name"
        required
        validationState={errors.name ? 'error' : undefined}
        validationMessage={errors.name?.message}
      >
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="azure-devops-connection-form-name"
              value={field.value ?? ''}
              placeholder="My Azure DevOps Organization"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <Field
        label="Organization URL"
        required
        validationState={errors.organizationUrl ? 'error' : undefined}
        validationMessage={errors.organizationUrl?.message}
        hint="e.g., https://dev.azure.com/your-org or https://your-org.visualstudio.com"
      >
        <Controller
          name="organizationUrl"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="azure-devops-connection-form-organizationUrl"
              value={field.value ?? ''}
              placeholder="https://dev.azure.com/your-organization"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <Field label="Authentication Type" required>
        <Controller
          name="authType"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onChange={(_, data) => field.onChange(data.value)}
              onBlur={field.onBlur}
              disabled={createConnection.isPending}
              data-testid="azure-devops-connection-form-authType"
            >
              {AZURE_DEVOPS_AUTH_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        />
      </Field>

      <Field
        label={authType === 'PersonalAccessToken' ? 'Personal Access Token' : 'Access Token'}
        required
        validationState={errors.accessToken ? 'error' : undefined}
        validationMessage={errors.accessToken?.message}
        hint="Token requires Code (read, status) scope. Full access recommended for all features."
      >
        <Controller
          name="accessToken"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="password"
              data-testid="azure-devops-connection-form-accessToken"
              value={field.value ?? ''}
              placeholder="Your PAT or access token"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <FormActions
        mode="create"
        testIdPrefix="azure-devops-connection-form"
        isSubmitting={createConnection.isPending}
        onCancel={onCancel}
        submitLabel="Create Connection"
        submittingLabel="Creating…"
        submitIcon={<PlugConnectedAddRegular />}
      />
    </form>
  );
};
